// src/pages/admin/SuperAdminDashboard.jsx
// Platform dashboard: companies, users and activity across all accounts.
// Tabbed layout: Overview / Users / Activities / Accounts. Layout modal preserved.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { subscriptionRequestService } from '../../services/apiServices';
import { dashboardService } from '../../services/dashboardServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LayoutSettingsModal from '../../components/dashboard/LayoutSettingsModal';
import { TabBar, PeriodSelector } from '../../components/dashboard/admin/shared';
import SuperOverview from '../../components/dashboard/admin/SuperOverview';
import SuperUsers from '../../components/dashboard/admin/SuperUsers';
import SuperActivities from '../../components/dashboard/admin/SuperActivities';
import SuperAccounts from '../../components/dashboard/admin/SuperAccounts';

const toFunnelRows = (f) => {
  if (Array.isArray(f)) return f;
  if (f && typeof f === 'object') {
    const out = [];
    Object.entries(f).forEach(([group, values]) => {
      if (values && typeof values === 'object' && !Array.isArray(values)) {
        Object.entries(values).forEach(([name, value]) => {
          out.push({ name, value: Number(value) || 0 });
        });
      } else if (typeof values === 'number') {
        out.push({ name: group, value: values });
      }
    });
    return out;
  }
  return [];
};

// Backend sends atRiskCompanies as { expiringSoon, idleOver30d } — prefer the
// pre-flattened atRiskCompaniesList array, else flatten with a reason per row.
const toAtRiskRows = (rawData) => {
  if (Array.isArray(rawData.atRiskCompaniesList) && rawData.atRiskCompaniesList.length) {
    return rawData.atRiskCompaniesList;
  }
  const obj = rawData.atRiskCompanies;
  if (Array.isArray(obj)) return obj;
  if (obj && typeof obj === 'object') {
    return [
      ...(obj.expiringSoon || []).map((c) => ({
        ...c,
        reason: c.reason || (c.subscriptionEndDate
          ? `Subscription expiring on ${String(c.subscriptionEndDate).slice(0, 10)}`
          : 'Subscription expiring soon'),
        updatedAt: c.updatedAt || c.subscriptionEndDate || null
      })),
      ...(obj.idleOver30d || []).map((c) => ({
        ...c,
        reason: c.reason || 'No activity for over 30 days',
        updatedAt: c.updatedAt || null
      }))
    ];
  }
  return [];
};

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const tr = (key, opts) => t(`superAdminDashboard.${key}`, opts);

  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState(7);
  const [raw, setRaw] = useState(null);
  const [previous, setPrevious] = useState({});
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const [widgetLayout, setWidgetLayout] = useState({
    metrics: { visible: true, position: 0 },
    growth: { visible: true, position: 1 },
    funnel: { visible: true, position: 2 },
    atRisk: { visible: true, position: 3 }
  });

  const fetchData = useCallback(async (days) => {
    try {
      const response = await dashboardService.getSuperAdminMetrics();
      if (!response.success) throw new Error('Failed to fetch dashboard metrics');
      setRaw(response.data || {});
      try {
        const hist = await dashboardService.getSuperAdminHistoricalMetrics(days);
        if (hist.success) setPrevious(hist.data.previousPeriod || {});
      } catch {
        setPrevious({});
      }
      try {
        const reqs = await subscriptionRequestService.getAllRequests();
        if (reqs.success) {
          setPendingRequests((reqs.data || []).filter((r) => r.status === 'pending'));
        }
      } catch {
        setPendingRequests([]);
      }
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      setError(true);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchData(period).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(period);
    setIsRefreshing(false);
  };

  const view = useMemo(() => {
    if (!raw) return null;
    const statuses = raw.companyStatuses || {};
    const funnel = toFunnelRows(raw.funnel || raw.companiesByStatus || [
      { name: 'Active', value: statuses.active || 0 },
      { name: 'Inactive', value: statuses.inactive || 0 },
      ...(statuses.trial != null ? [{ name: 'Trial', value: statuses.trial }] : []),
      ...(statuses.expired != null ? [{ name: 'Expired', value: statuses.expired }] : [])
    ]);
    const engagement = raw.userEngagement || [];
    const wau = raw.weeklyActiveUsers
      ?? raw.wau
      ?? (engagement.length ? engagement.reduce((s, d) => s + (Number(d.activeUsers) || 0), 0) : null);
    return {
      metrics: {
        totalCompanies: raw.totalCompanies || 0,
        totalUsers: raw.totalUsers || 0,
        totalAnalyses: raw.totalAnalyses ?? raw.activeAnalyses ?? 0,
        weeklyActiveUsers: wau,
        aiCostTotal: raw.aiUsage?.totalCost ?? raw.aiCostTotal ?? null
      },
      previous: {
        totalCompanies: previous.totalCompanies || 0,
        totalUsers: previous.totalUsers || 0,
        totalAnalyses: previous.totalAnalyses ?? previous.activeAnalyses ?? 0,
        weeklyActiveUsers: previous.weeklyActiveUsers ?? previous.wau ?? null,
        aiCostTotal: previous.aiUsage?.totalCost ?? previous.aiCostTotal ?? null,
        pendingRequests: previous.pendingRequests ?? null
      },
      pendingCount: pendingRequests.length,
      pendingRequests,
      usersByRole: raw.usersByRole || {},
      activityTrends: raw.activityTrends || [],
      recentActivities: raw.recentActivities || [],
      funnel,
      // Newer backend fields — every tab falls back gracefully when absent.
      atRiskCompanies: toAtRiskRows(raw),
      topCompanies: raw.topCompaniesByUsers || raw.topCompanies || [],
      topCompaniesActivity: raw.topCompaniesByActivity || [],
      companiesByMonth: raw.companiesByMonth || raw.newCompaniesPerMonth || [],
      aiByCompany: raw.aiUsage?.byCompany || raw.aiCostByCompany || []
    };
  }, [raw, previous, pendingRequests]);

  const tabs = [
    { id: 'overview', label: tr('tabs.overview') },
    { id: 'users', label: tr('tabs.users') },
    { id: 'activities', label: tr('tabs.activities') },
    { id: 'accounts', label: tr('tabs.accounts') }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
        <p className="font-medium">Erreur</p>
        <p>{tr('loadError')}</p>
        <button onClick={() => window.location.reload()} className="mt-2 bg-red-100 px-3 py-1 rounded-lg hover:bg-red-200">
          {tr('refresh')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Super Administrateur</h1>
        <div className="flex flex-wrap items-center gap-2">
          {updatedAt && (
            <span className="text-xs text-slate-500 bg-white px-3 py-2 rounded-lg border border-slate-200">
              {tr('lastUpdated')} : {updatedAt.toLocaleString()}
            </span>
          )}
          <PeriodSelector value={period} onChange={setPeriod}
            labels={{ 7: tr('period7'), 30: tr('period30'), 90: tr('period90') }} />
          <button onClick={handleRefresh} disabled={isRefreshing} title={tr('refresh')}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 bg-white border border-slate-200 transition-colors">
            <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button onClick={() => setShowLayoutSettings(true)} title={tr('customize')}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 bg-white border border-slate-200 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      {showLayoutSettings && (
        <LayoutSettingsModal
          widgetLayout={widgetLayout}
          setWidgetLayout={setWidgetLayout}
          onClose={() => setShowLayoutSettings(false)}
        />
      )}

      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && <SuperOverview view={view} t={tr} period={period} layout={widgetLayout} />}
      {activeTab === 'users' && <SuperUsers view={view} t={tr} />}
      {activeTab === 'activities' && <SuperActivities view={view} t={tr} period={period} />}
      {activeTab === 'accounts' && <SuperAccounts view={view} t={tr} />}
    </div>
  );
};

export default SuperAdminDashboard;
